import { Badge } from "@d12labs/ui/badge";
import { Button } from "@d12labs/ui/button";
import { ConfirmDialog } from "@d12labs/ui/confirm-dialog";
import { List, ListHeader, ListRow } from "@d12labs/ui/data-list";
import { EmptyState } from "@d12labs/ui/empty-state";
import { PageBody, PageHeader } from "@d12labs/ui/page-header";
import { Pill } from "@d12labs/ui/pill";
import { cn } from "@d12labs/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
	Calendar,
	ChevronDown,
	ChevronRight,
	ChevronUp,
	Clock,
	Plus,
	RefreshCw,
	Trash2,
	Users,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { meetingPerfApi } from "../api";
import { RoleBadge } from "../components/role-badge";
import { meetingTypeLabel, overallScoreColor } from "../types";

type SortKey = "title" | "date" | "duration" | "speakers" | "score";
type SortDir = "asc" | "desc";
/** First-click direction per column — text ascends, numbers/dates descend. */
const INITIAL_DIR: Record<SortKey, SortDir> = {
	title: "asc",
	date: "desc",
	duration: "desc",
	speakers: "desc",
	score: "desc",
};

/** A row/header checkbox that never triggers the surrounding row-`Link`. */
function SelectBox({
	checked,
	indeterminate,
	onToggle,
	label,
}: {
	checked: boolean;
	indeterminate?: boolean;
	onToggle: () => void;
	label: string;
}) {
	return (
		<input
			type="checkbox"
			aria-label={label}
			checked={checked}
			ref={(el) => {
				if (el) el.indeterminate = indeterminate ?? false;
			}}
			// Stop the click from reaching the row `Link`'s handler so selecting
			// never navigates to the meeting detail.
			onClick={(e) => e.stopPropagation()}
			onChange={onToggle}
			className="h-3.5 w-3.5 cursor-pointer accent-primary"
		/>
	);
}

export function MeetingsListPage() {
	const qc = useQueryClient();
	// null = All (no filter); a string value = specific type; "__untagged__" sentinel for untagged
	const [activeType, setActiveType] = useState<string | null>(null);
	// Empty = meetings with no speakers and no score (server `isEmpty`).
	const [emptyOnly, setEmptyOnly] = useState(false);
	const [sort, setSort] = useState<SortKey>("date");
	const [dir, setDir] = useState<SortDir>("desc");
	// Multi-select for bulk delete — ids of the currently-selected rows.
	const [selected, setSelected] = useState<Set<string>>(new Set());
	// Id of the row whose transcript re-fetch is in-flight (per-row disable).
	const [refetchingId, setRefetchingId] = useState<string | null>(null);

	const toggleSort = (key: SortKey) => {
		if (sort === key) {
			setDir((d) => (d === "asc" ? "desc" : "asc"));
		} else {
			setSort(key);
			setDir(INITIAL_DIR[key]);
		}
	};

	/** Clickable header label with the active-column direction indicator. */
	const sortLabel = (key: SortKey, text: string, align?: "right") => (
		<button
			type="button"
			onClick={() => toggleSort(key)}
			className={cn(
				"flex w-full items-center gap-0.5 transition-colors hover:text-foreground",
				align === "right" && "justify-end",
			)}
		>
			{text}
			{sort === key ? (
				dir === "asc" ? (
					<ChevronUp className="h-3 w-3 flex-none" />
				) : (
					<ChevronDown className="h-3 w-3 flex-none" />
				)
			) : null}
		</button>
	);

	const meetingTypeParam = activeType ?? undefined;
	const isDefaultSort = sort === "date" && dir === "desc";

	const meetings = useQuery({
		// Include filter + sort state in the key so changing either refetches.
		queryKey: ["meeting-perf", "meetings", "list", activeType ?? "all", emptyOnly, sort, dir],
		queryFn: () =>
			meetingPerfApi.listMeetings({
				query: {
					pageSize: 100,
					...(meetingTypeParam ? { meetingType: meetingTypeParam } : {}),
					...(emptyOnly ? { empty: true } : {}),
					...(isDefaultSort ? {} : { sort, dir }),
				},
			}),
	});

	// Fetch the unfiltered list separately so pill options stay stable during a filtered fetch.
	// Note: pageSize=100 limits distinct-type discovery to the first page — pre-existing constraint.
	const allMeetings = useQuery({
		queryKey: ["meeting-perf", "meetings", "list", "all"],
		queryFn: () => meetingPerfApi.listMeetings({ query: { pageSize: 100 } }),
	});

	const items = meetings.data?.items ?? [];

	// Selection is scoped to the loaded page; clear it whenever the filter/sort
	// changes so a stale id set can't survive into a different result set.
	// biome-ignore lint/correctness/useExhaustiveDependencies: reset-on-key pattern
	useEffect(() => {
		setSelected(new Set());
	}, [activeType, emptyOnly, sort, dir]);

	const toggleOne = (id: string) =>
		setSelected((prev) => {
			const next = new Set(prev);
			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}
			return next;
		});

	const allVisibleSelected = items.length > 0 && items.every((m) => selected.has(m.id));
	const someVisibleSelected = items.some((m) => selected.has(m.id));
	const toggleAllVisible = () =>
		setSelected((prev) => {
			// If every visible row is selected, clear the visible ones; else add them all.
			const next = new Set(prev);
			if (allVisibleSelected) {
				for (const m of items) next.delete(m.id);
			} else {
				for (const m of items) next.add(m.id);
			}
			return next;
		});

	const bulkDelete = useMutation({
		mutationFn: (ids: string[]) => meetingPerfApi.bulkRemoveMeetings({ input: { ids } }),
		onSuccess: (res) => {
			toast.success(`Deleted ${res.deleted} meeting${res.deleted === 1 ? "" : "s"}`);
			setSelected(new Set());
			// Refetch both the filtered list and the unfiltered pill-option list.
			qc.invalidateQueries({ queryKey: ["meeting-perf", "meetings"] });
		},
		onError: () => toast.error("Could not delete the selected meetings."),
	});

	// Manual transcript re-fetch (D12-88) — re-runs the repair engine on a pending
	// meeting. Tracks the in-flight row id so only that row's button disables.
	const refetch = useMutation({
		mutationFn: (id: string) => meetingPerfApi.refetchTranscript({ params: { id } }),
		onMutate: (id) => setRefetchingId(id),
		onSuccess: (res) => {
			if (res.healed) {
				toast.success("Transcript recovered");
			} else {
				toast.info("Still no transcript yet");
			}
			qc.invalidateQueries({ queryKey: ["meeting-perf", "meetings"] });
		},
		onError: () => toast.error("Re-fetch failed"),
		onSettled: () => setRefetchingId(null),
	});

	// Derive pill options from the unfiltered list.
	const pillOptions = useMemo(() => {
		const allItems = allMeetings.data?.items ?? [];
		const types = new Set<string>();
		let hasUntagged = false;
		for (const m of allItems) {
			const mt = m.metadata.meetingType?.trim();
			if (mt) {
				types.add(mt);
			} else {
				hasUntagged = true;
			}
		}
		const opts: { label: string; value: string | null }[] = [{ label: "All", value: null }];
		for (const t of [...types].sort()) opts.push({ label: t, value: t });
		if (hasUntagged) opts.push({ label: "Untagged", value: "__untagged__" });
		return opts;
	}, [allMeetings.data]);

	const showTypePills = pillOptions.length > 1;
	const selectedCount = selected.size;

	return (
		<>
			<PageHeader
				icon={Calendar}
				title="Meetings"
				subtitle="Every analyzed conversation in this workspace."
				badges={meetings.data ? <Badge tone="subtle">{meetings.data.total}</Badge> : undefined}
				actions={
					<Link to="/meeting-perf/upload">
						<Button size="sm">
							<Plus className="h-3.5 w-3.5" /> New meeting
						</Button>
					</Link>
				}
			/>
			<PageBody className="p-6">
				<div className="mx-auto w-full max-w-5xl">
					{meetings.isLoading ? <p className="text-body-sm text-ink-subtle">Loading…</p> : null}
					{meetings.error ? (
						<p className="text-body-sm text-destructive">Could not load meetings.</p>
					) : null}

					{/* Filters: meeting-type pills (when present) + the empty-only toggle. */}
					<div className="mb-4 flex flex-wrap items-center gap-1.5">
						{showTypePills
							? pillOptions.map((opt) => (
									<Pill
										key={opt.value ?? "all"}
										active={activeType === opt.value}
										onClick={() => setActiveType(opt.value)}
									>
										{opt.label}
									</Pill>
								))
							: null}
						{showTypePills ? <span className="mx-1 h-4 w-px bg-hairline" /> : null}
						<Pill active={emptyOnly} onClick={() => setEmptyOnly((v) => !v)}>
							Empty (no transcript/score)
						</Pill>
					</div>

					{/* Bulk-action bar — only when at least one row is selected. */}
					{selectedCount > 0 ? (
						<div className="mb-3 flex items-center gap-3 rounded-md border border-hairline bg-surface-1 px-3 py-2">
							<span className="text-body-sm text-ink">{selectedCount} selected</span>
							<ConfirmDialog
								title={`Delete ${selectedCount} meeting${selectedCount === 1 ? "" : "s"}?`}
								description="This permanently removes the selected meetings, their analyses and stored recordings. Fireflies meetings won't be imported again — find them under Import from Fireflies → Removed to restore one."
								destructive
								confirmLabel="Delete"
								onConfirm={() => bulkDelete.mutate([...selected])}
							>
								<Button variant="destructive" size="sm" disabled={bulkDelete.isPending}>
									<Trash2 className="h-3.5 w-3.5" /> Delete selected ({selectedCount})
								</Button>
							</ConfirmDialog>
							<Button
								variant="ghost"
								size="sm"
								onClick={() => setSelected(new Set())}
								disabled={bulkDelete.isPending}
							>
								Clear
							</Button>
						</div>
					) : null}

					{meetings.data ? (
						items.length === 0 ? (
							emptyOnly ? (
								// Empty-only filter, nothing to clean up.
								<List>
									<EmptyState
										icon={Calendar}
										title="No empty meetings"
										body="Nothing to clean up — every meeting has speakers or a score."
										primary={
											<Button variant="ghost" size="sm" onClick={() => setEmptyOnly(false)}>
												Clear filter
											</Button>
										}
									/>
								</List>
							) : activeType !== null ? (
								// Filtered empty state
								<List>
									<EmptyState
										icon={Calendar}
										title="No meetings match this filter"
										body={`No meetings tagged as "${activeType === "__untagged__" ? "Untagged" : activeType}".`}
										primary={
											<Button variant="ghost" size="sm" onClick={() => setActiveType(null)}>
												Clear filter
											</Button>
										}
									/>
								</List>
							) : (
								<List>
									<EmptyState
										icon={Calendar}
										title="No meetings yet"
										body="Upload a transcript to start tracking your conversations."
										primary={
											<Link to="/meeting-perf/upload">
												<Button>
													<Plus className="h-4 w-4" /> New meeting
												</Button>
											</Link>
										}
									/>
								</List>
							)
						) : (
							<List>
								<ListHeader
									columns={[
										{
											label: (
												<SelectBox
													label="Select all meetings"
													checked={allVisibleSelected}
													indeterminate={someVisibleSelected && !allVisibleSelected}
													onToggle={toggleAllVisible}
												/>
											),
											width: "28px",
										},
										{ label: sortLabel("title", "Meeting"), width: "1fr" },
										{ label: sortLabel("date", "Date"), width: "110px" },
										{
											label: sortLabel("duration", "Duration", "right"),
											width: "80px",
											align: "right",
										},
										{
											label: sortLabel("speakers", "Speakers", "right"),
											width: "80px",
											align: "right",
										},
										{ label: sortLabel("score", "Score", "right"), width: "64px", align: "right" },
										{ label: "", width: "24px" },
									]}
								/>
								{items.map((m) => {
									const dateStr = new Date(m.meetingDate).toLocaleDateString(undefined, {
										month: "short",
										day: "numeric",
										year: "numeric",
									});
									const mt = m.metadata.meetingType?.trim();
									return (
										<ListRow
											key={m.id}
											cells={[
												{
													value: (
														<SelectBox
															label={`Select ${m.title}`}
															checked={selected.has(m.id)}
															onToggle={() => toggleOne(m.id)}
														/>
													),
													width: "28px",
												},
												{
													value: (
														<div className="flex items-center gap-2">
															<span className="truncate text-body-sm font-medium text-ink">
																{m.title}
															</span>
															{m.source === "fireflies" ? (
																<Badge tone="subtle">Fireflies</Badge>
															) : null}
															{m.isEmpty ? <Badge tone="warning">Empty</Badge> : null}
															{mt ? <Badge tone="neutral">{meetingTypeLabel(mt)}</Badge> : null}
															<RoleBadge role={m.involvement ?? null} />
															{m.transcriptStatus === "pending" ? (
																<button
																	type="button"
																	aria-label={`Re-fetch transcript for ${m.title}`}
																	title="Re-fetch transcript"
																	disabled={refetchingId === m.id}
																	// Stop the click from reaching the row `Link` so re-fetching
																	// never navigates to the meeting detail.
																	onClick={(e) => {
																		e.preventDefault();
																		e.stopPropagation();
																		refetch.mutate(m.id);
																	}}
																	className="flex h-5 w-5 flex-none items-center justify-center rounded text-ink-subtle transition-colors hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
																>
																	<RefreshCw
																		className={cn(
																			"h-3 w-3",
																			refetchingId === m.id && "animate-spin",
																		)}
																	/>
																</button>
															) : null}
														</div>
													),
													width: "1fr",
												},
												{
													value: (
														<span className="flex items-center gap-1 text-meta text-ink-subtle">
															<Calendar className="h-3 w-3 flex-none" />
															{dateStr}
														</span>
													),
													width: "110px",
												},
												{
													value: m.durationMinutes ? (
														<span className="flex items-center justify-end gap-1 text-meta text-ink-subtle">
															<Clock className="h-3 w-3 flex-none" />
															{m.durationMinutes}m
														</span>
													) : (
														<span className="text-meta text-ink-tertiary">—</span>
													),
													width: "80px",
													align: "right",
												},
												{
													value: m.participantCount ? (
														<span className="flex items-center justify-end gap-1 text-meta text-ink-subtle">
															<Users className="h-3 w-3 flex-none" />
															{m.participantCount}
														</span>
													) : (
														<span className="text-meta text-ink-tertiary">—</span>
													),
													width: "80px",
													align: "right",
												},
												{
													value:
														m.score != null ? (
															<Badge tone="subtle">
																<span className={cn("tabular-nums", overallScoreColor(m.score))}>
																	{m.score}
																</span>
															</Badge>
														) : (
															<span className="text-meta text-ink-tertiary">—</span>
														),
													width: "64px",
													align: "right",
												},
												{
													value: <ChevronRight className="h-3.5 w-3.5 text-ink-subtle" />,
													width: "24px",
													align: "right",
												},
											]}
											render={(p) => (
												<Link to="/meeting-perf/meetings/$id" params={{ id: m.id }} {...p} />
											)}
										/>
									);
								})}
							</List>
						)
					) : null}
				</div>
			</PageBody>
		</>
	);
}
